import { companyRolesAttributesDtoRolesEnum } from "@dc/gateway-jsonapi";
import { freeEmailDomainOf } from "@dc/shared/free-email-domains";
import { countryHasRegions, STATE_NOT_APPLICABLE } from "@dc/ui/components";
import { z } from "zod";

export const selectableCompanyRolesEnum = Object.values(companyRolesAttributesDtoRolesEnum).filter(
  (value) => value !== "Other",
);

export interface RegisterTrialPublicValidationMessages {
  firstNameRequired: string;
  lastNameRequired: string;
  emailRequired: string;
  emailInvalid: string;
  personalEmail: (domain: string) => string;
  companyRequired: string;
  companyTooLong: string;
  phoneRequired: string;
  phoneTooLong: string;
  countryRequired: string;
  stateRequired: string;
  termsRequired: string;
  customRoleRequired: string;
  customRoleTooLong: string;
}

export const createRegisterTrialPublicFormSchema = (messages: RegisterTrialPublicValidationMessages) => {
  const email = z
    .string()
    .trim()
    .toLowerCase()
    .min(1, messages.emailRequired)
    .pipe(z.email(messages.emailInvalid))
    .superRefine((value, ctx) => {
      const domain = freeEmailDomainOf(value);
      if (!domain) return;
      ctx.addIssue({ code: "custom", message: messages.personalEmail(domain) });
    })
    .prefault("");

  const roleSchema = z.discriminatedUnion("companyRole", [
    z
      .object({
        companyRole: z.literal("Other"),
        customCompanyRole: z
          .string()
          .trim()
          .min(1, messages.customRoleRequired)
          .max(100, messages.customRoleTooLong)
          .prefault(""),
      })
      .transform(({ customCompanyRole }) => ({ companyRole: customCompanyRole })),
    z.object({
      companyRole: z.enum(selectableCompanyRolesEnum).optional(),
    }),
  ]);

  return z
    .object({
      firstName: z.string().trim().min(1, messages.firstNameRequired).prefault(""),
      lastName: z.string().trim().min(1, messages.lastNameRequired).prefault(""),
      email,
      company: z.string().trim().min(1, messages.companyRequired).max(100, messages.companyTooLong).prefault(""),
      acceptedTerms: z
        .boolean()
        .refine((value) => value === true, { error: messages.termsRequired })
        .prefault(false),
      phone: z.string().trim().min(1, messages.phoneRequired).max(50, messages.phoneTooLong).prefault(""),
      country: z.string().trim().min(1, messages.countryRequired).prefault(""),
      state: z.string().trim().prefault(""),
      marketingOptIn: z.boolean().prefault(false),
      password: z.string().optional(),
      confirmPassword: z.string().optional(),
    })
    .and(roleSchema)
    .refine((data) => !countryHasRegions(data.country) || data.state.length > 0, {
      path: ["state"],
      error: messages.stateRequired,
    })
    .transform(({ acceptedTerms, company: companyName, companyRole, phone, state, ...data }) => ({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone,
      company: companyName,
      ...(companyRole ? { companyRole } : {}),
      country: data.country,
      // HubSpot's state picklist only covers the US and Canada; N/A is its own option.
      state: state || STATE_NOT_APPLICABLE,
      marketingOptIn: data.marketingOptIn,
      acceptedTerms,
    }));
};

type RegisterTrialPublicFormSchema = ReturnType<typeof createRegisterTrialPublicFormSchema>;

export type RegisterTrialPublicFormInput = z.input<RegisterTrialPublicFormSchema>;
export type RegisterTrialPublicFormValues = z.output<RegisterTrialPublicFormSchema>;
export type TrialLeadFormValues = RegisterTrialPublicFormValues;
